import { useCallback } from 'react';
import { useCommandDialog } from '@/features/command-dialog';
import { useAnalytics } from '@/services/analytics';
import { PlainButton, KeyboardShortcut, TextField, Box } from '@customerio/pluma-components/react';
import * as topNavStyles from '../../index.css';
import { useQueryParamAction } from '@/hooks/use-query-param-action';
import { useHotkey } from '@tanstack/react-hotkeys';

export function CommandDialogButton() {
  const { isOpen, open, close, getCloseTrackingSnapshot } = useCommandDialog();
  const { track } = useAnalytics();

  const handleShortcutClose = useCallback(() => {
    const { hadQuery, timeOpenMs } = getCloseTrackingSnapshot();
    void track('universalSearch', 'closed', {
      Reason: 'shortcut',
      'Had Query': hadQuery,
      'Time Open Ms': timeOpenMs,
    });
    close();
  }, [close, getCloseTrackingSnapshot, track]);

  const handleOpen = useCallback(
    (entryPoint: 'header' | 'shortcut' = 'header') => {
      if (isOpen) {
        return;
      }

      void track('universalSearch', 'opened', { 'Entry Point': entryPoint });
      open();
    },
    [isOpen, open, track],
  );

  // https://tanstack.com/hotkeys/latest/docs/framework/react/guides/hotkeys#preventdefault
  // preventDefault is set to true by default
  useHotkey('Mod+K', () => {
    if (isOpen) {
      handleShortcutClose();
    } else {
      handleOpen('shortcut');
    }
  });

  // Deep-link support: ?cmd-search=<query> opens the dialog with a prefilled query
  // Note: uses 'cmd-search' instead of 'search' to avoid collision with deliverability page's ?search= param
  useQueryParamAction('cmd-search', (value) => {
    void track('universalSearch', 'opened', { 'Entry Point': 'deep_link' });
    open({ initialInputValue: value });
  });

  return (
    <Box className={topNavStyles.searchPaletteInputContainer}>
      <PlainButton
        className={topNavStyles.searchPaletteInputButton}
        onClick={(event) => {
          event.preventDefault();
          handleOpen();
        }}
      >
        <TextField
          ariaLabel="Search"
          placeholder="Search…"
          icon="search"
          size="sm"
          unsafe_inputWrapperClassName={topNavStyles.searchPaletteInputWrapper}
          unsafe_rightSectionComponent={<KeyboardShortcut hotkey="Mod+K" mr="050" />}
          readOnly={true}
          onClick={(event) => {
            event.preventDefault();
          }}
          flexGrow="1"
        />
      </PlainButton>
    </Box>
  );
}
